import { useState, useRef, useEffect } from 'react';
import { Outlet, NavLink, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  LayoutDashboard,
  Package,
  MessageSquare,
  User,
  LogOut,
  Menu,
  X,
  Plus,
  Bell,
  FileText,
  BellOff,
  Check,
  Search,
  Users,
  ShieldCheck,
  BarChart3,
  FileCheck2,
  CreditCard,
  Sparkles,
  Trophy,
  ChevronRight,
  Inbox,
  Instagram,
  Megaphone,
  Bookmark,
  Gift,
  HeartHandshake,
  MessageSquareHeart,
} from 'lucide-react';
import { InstallMenuItem } from '@/components/pwa';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api';
import { parseFirestoreTimestamp, type FirestoreTimestampLike } from '@/lib/timestamp';
import { formatDate } from '@/lib/utils';
import { FEATURES } from '@/lib/features';
import { Logo, SiteFooter, ThemeToggle } from '@/components/ui';
import { FeedbackButton } from '@/components/ui/FeedbackButton';
import { useHasUnseenNovidades } from '@/lib/novidadesSeen';
import { NotificationIcon } from '@/components/ui/NotificationIcon';
import { AdminMfaRequired } from '@/components/admin/AdminMfaRequired';
import { useAdminMfaStore } from '@/stores/adminMfaStore';
import type { Notification } from '@patrociconnect/shared';

const ROLE_LABELS: Record<string, string> = {
  seeker: 'Buscando Patrocínio',
  sponsor: 'Patrocinador',
  superadmin: 'Administrador',
};

const ROLE_COLORS: Record<string, string> = {
  seeker: 'bg-brand-400/15 text-brand-300 ring-1 ring-inset ring-brand-400/20',
  sponsor: 'bg-amber-500/15 text-amber-300 ring-1 ring-inset ring-amber-400/20',
  superadmin: 'bg-red-500/15 text-red-300 ring-1 ring-inset ring-red-400/20',
};

export function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const { user, signOut } = useAuthStore();
  const isMfaRequired = useAdminMfaStore((state) => state.isMfaRequired);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isSeeker = user?.role === 'seeker';
  const isSponsor = user?.role === 'sponsor';
  const isAdmin = user?.role === 'superadmin';

  const { data: notificationsData } = useQuery({
    queryKey: ['notifications', user?.uid],
    queryFn: async () => {
      return api.get<{ notifications: Notification[]; unreadCount: number }>('/notifications?limit=10&unreadOnly=true');
    },
    enabled: !!user?.uid && !isAdmin,
  });

  const notifications = (notificationsData?.notifications ?? []).filter((n) => !n.read);
  const unreadCount = notificationsData?.unreadCount ?? 0;

  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      return api.patch(`/notifications/${notificationId}/read`, {});
    },
    onMutate: async (notificationId: string) => {
      await queryClient.cancelQueries({ queryKey: ['notifications', user?.uid] });
      const previous = queryClient.getQueryData<{ notifications: Notification[]; unreadCount: number }>([
        'notifications',
        user?.uid,
      ]);
      if (previous) {
        queryClient.setQueryData(['notifications', user?.uid], {
          notifications: previous.notifications.filter((n) => n.id !== notificationId),
          unreadCount: Math.max(0, previous.unreadCount - 1),
        });
      }
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(['notifications', user?.uid], ctx.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-all'] });
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      return api.post('/notifications/read-all', {});
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['notifications', user?.uid] });
      const previous = queryClient.getQueryData<{ notifications: Notification[]; unreadCount: number }>([
        'notifications',
        user?.uid,
      ]);
      queryClient.setQueryData(['notifications', user?.uid], {
        notifications: [],
        unreadCount: 0,
      });
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(['notifications', user?.uid], ctx.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-all'] });
    },
  });

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setNotificationsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAsRead = (notificationId: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    markAsReadMutation.mutate(notificationId);
  };

  const markAllAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  const getNotificationLink = (notification: Notification): string | null => {
    const data = notification.data as Record<string, string> | undefined;
    switch (notification.type) {
      case 'new_offer':
        return data?.offerId ? `/dashboard/propostas?offerId=${data.offerId}` : '/dashboard/propostas';
      case 'offer_accepted':
      case 'offer_rejected':
        return data?.offerId ? `/dashboard/ofertas?offerId=${data.offerId}` : '/dashboard/ofertas';
      case 'new_message':
        return data?.chatId ? `/dashboard/negociacoes?chatId=${data.chatId}` : '/dashboard/negociacoes';
      case 'match_alert':
        return '/dashboard/buscas';
      case 'kyc_approved':
      case 'kyc_rejected':
      case 'kyc_suspended':
        return '/dashboard/perfil?aba=kyc';
      case 'ranking_change':
        return FEATURES.ranking ? '/dashboard/ranking' : null;
      case 'engagement_milestone':
      case 'card_published':
        if (!FEATURES.stickerCards) return null;
        return data?.cardId ? `/dashboard/criador-de-posts/${data.cardId}` : '/dashboard/criador-de-posts';
      case 'new_season':
        return FEATURES.stickerCards ? '/dashboard/criador-de-posts' : null;
      default:
        return null;
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      markAsReadMutation.mutate(notification.id);
    }
    const link = getNotificationLink(notification);
    if (link) {
      setNotificationsOpen(false);
      navigate(link);
    }
  };

  const formatTimeAgo = (timestamp: FirestoreTimestampLike) => {
    const date = parseFirestoreTimestamp(timestamp);
    if (!date) return '';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'agora';
    if (diffMins < 60) return `${diffMins}min`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return formatDate(date);
  };

  const hasUnseenNovidades = useHasUnseenNovidades();
  interface NavItem {
    name: string;
    href: string;
    icon: typeof LayoutDashboard;
    isNew?: boolean;
    badge?: number;
  }
  const navGroups: Array<{ title: string | null; items: NavItem[] }> = [
    {
      title: null,
      items: [
        { name: 'Início', href: '/dashboard', icon: LayoutDashboard },
        ...((isSeeker || isSponsor) ? [
          { name: 'Explorar', href: '/dashboard/explorar', icon: Search },
          { name: 'Notificações', href: '/dashboard/notificacoes', icon: Bell, badge: unreadCount },
        ] : []),
      ],
    },
    ...(isSeeker ? [{
      title: 'Captação',
      items: [
        { name: 'Minhas oportunidades', href: '/dashboard/assets', icon: Package },
        { name: 'Chamadas abertas', href: '/dashboard/buscar-patrocinio', icon: Megaphone },
        { name: 'Minhas candidaturas', href: '/dashboard/candidaturas', icon: FileCheck2 },
        { name: 'Propostas recebidas', href: '/dashboard/propostas', icon: FileText },
      ],
    }] : []),
    ...(isSponsor ? [{
      title: 'Patrocínio',
      items: [
        { name: 'Minhas chamadas', href: '/dashboard/chamadas', icon: Megaphone },
        { name: 'Propostas enviadas', href: '/dashboard/ofertas', icon: FileText },
        { name: 'Buscas salvas', href: '/dashboard/buscas', icon: Bookmark },
      ],
    }] : []),
    ...((isSeeker || isSponsor) ? [{
      title: 'Negócios',
      items: [
        { name: 'Negociações', href: '/dashboard/negociacoes', icon: MessageSquare },
        { name: 'Acordos', href: '/dashboard/acordos', icon: HeartHandshake },
        ...(isSeeker && FEATURES.stickerCards ? [{ name: 'Criador de posts', href: '/dashboard/criador-de-posts', icon: Sparkles }] : []),
        ...(isSeeker && FEATURES.ranking ? [{ name: 'Ranking', href: '/dashboard/ranking', icon: Trophy }] : []),
      ],
    }] : []),
    ...(isAdmin ? [{
      title: 'Administração',
      items: [
        { name: 'Usuários', href: '/dashboard/admin/usuarios', icon: Users },
        { name: 'Oportunidades', href: '/dashboard/admin/assets', icon: Package },
        { name: 'Verificações KYC', href: '/dashboard/admin/kyc', icon: ShieldCheck },
        { name: 'Verificação de audiência', href: '/dashboard/admin/audiencia', icon: Instagram },
        ...(FEATURES.ranking ? [{ name: 'Temporadas', href: '/dashboard/admin/temporadas', icon: Trophy }] : []),
        ...(FEATURES.stickerCards ? [{ name: 'Figurinhas', href: '/dashboard/admin/figurinhas', icon: Sparkles }] : []),
        { name: 'Relatórios', href: '/dashboard/admin/relatorios', icon: BarChart3 },
        { name: 'Central de ocorrências', href: '/dashboard/admin/ocorrencias', icon: Inbox },
      ],
    }] : []),
    {
      title: 'Conta',
      items: [
        { name: 'Meu perfil', href: '/dashboard/perfil', icon: User },
        ...((isSeeker || isSponsor) ? [{ name: 'Assinatura', href: '/dashboard/assinatura', icon: CreditCard }] : []),
        { name: 'Minhas sugestões', href: '/dashboard/feedback', icon: MessageSquareHeart },
        { name: 'Novidades', href: '/dashboard/novidades', icon: Gift, isNew: hasUnseenNovidades },
      ],
    },
  ];

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const getUserInitials = () => {
    if (!user?.email) return '?';
    return user.email.charAt(0).toUpperCase();
  };

  return (
    <div className="min-h-screen bg-canvas">
      <a href="#conteudo" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-lg focus:bg-elevated focus:px-4 focus:py-2 focus:shadow-elevation-3">
        Pular para o conteúdo
      </a>
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 transition-opacity lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-72 transform flex-col border-r border-line bg-elevated transition-transform duration-300 ease-out lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Logo */}
        <div className="flex h-16 items-center justify-between px-6">
          <Logo to="/" />
          <button
            type="button"
            className="rounded-lg p-1.5 text-muted hover:bg-ink/5 hover:text-ink lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Fechar menu lateral"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        {/* Quick action */}
        {isSeeker && (
          <div className="px-4 pb-2">
            <Link
              to="/dashboard/assets/novo"
              className="btn-primary w-full justify-center text-sm"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              Nova Oportunidade
            </Link>
          </div>
        )}
        {isSponsor && (
          <div className="px-4 pb-2">
            <Link to="/dashboard/explorar" className="btn-primary w-full justify-center text-sm">
              <Search className="h-4 w-4" />
              Explorar
            </Link>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-2" aria-label="Menu do painel">
          {navGroups.filter((group) => group.items.length > 0).map((group, index) => (
            <div key={group.title ?? 'principal'} className={cn(index > 0 && 'mt-5')}>
              {group.title && (
                <p className="mb-1 px-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted/80">{group.title}</p>
              )}
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    end={item.href === '/dashboard'}
                    className={({ isActive }) =>
                      cn(
                        'group flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors',
                        isActive ? 'bg-brand-400/10 font-semibold text-brand-300' : 'text-ink/70 hover:bg-ink/5 hover:text-ink'
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <item.icon
                          className={cn('h-[18px] w-[18px] shrink-0 transition-colors', isActive ? 'text-brand-400' : 'text-ink/40 group-hover:text-ink/80')}
                          aria-hidden="true"
                        />
                        <span className="flex-1 truncate">{item.name}</span>
                        {item.badge ? (
                          <span className="min-w-[20px] rounded-full bg-brand-500 px-1.5 py-0.5 text-center text-[10px] font-bold leading-none text-on-brand" aria-label={`${item.badge} não lidas`}>
                            {item.badge > 99 ? '99+' : item.badge}
                          </span>
                        ) : item.isNew && !isActive ? (
                          <span className="rounded-full bg-brand-500 px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none tracking-wide text-on-brand">
                            Novo
                          </span>
                        ) : null}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* User section */}
        <div className="border-t border-ink/[0.06] p-4">
          <InstallMenuItem className="mb-2" />
          <div className="flex items-center gap-3 rounded-xl p-2 transition-colors hover:bg-ink/5">
            <div className={cn(
              'flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold',
              ROLE_COLORS[user?.role ?? ''] ?? 'bg-ink/[0.06] text-ink/70 ring-1 ring-inset ring-ink/10'
            )}>
              {getUserInitials()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-ink">{user?.email}</p>
              <p className="text-xs text-ink/40">
                {ROLE_LABELS[user?.role ?? ''] ?? user?.role}
              </p>
            </div>
            <button
              onClick={handleSignOut}
              type="button"
              className="shrink-0 rounded-lg p-2 text-muted transition-colors hover:bg-ink/5 hover:text-danger"
              title="Sair"
              aria-label="Sair da conta"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex min-h-screen flex-col lg:pl-72">
        {/* Top bar */}
        <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-line bg-canvas/95 px-4 backdrop-blur lg:px-8">
          <button
            type="button"
            className="rounded-lg p-2 text-muted hover:bg-ink/5 hover:text-ink lg:hidden"
            onClick={() => setSidebarOpen(true)}
            aria-label="Abrir menu lateral"
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="flex-1" />

          <ThemeToggle />

          {/* Notifications */}
          {!isAdmin && (
            <div className="relative" ref={notificationsRef}>
              <button
                type="button"
                onClick={() => setNotificationsOpen(!notificationsOpen)}
                aria-label={unreadCount > 0 ? `Notificações: ${unreadCount} não lidas` : 'Notificações'}
                aria-expanded={notificationsOpen}
                className={cn(
                  'relative rounded-xl p-2 transition-all',
                  notificationsOpen
                    ? 'bg-brand-400/10 text-brand-400'
                    : 'text-ink/50 hover:bg-ink/5 hover:text-ink'
                )}
              >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 flex h-4.5 w-4.5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white ring-2 ring-canvas">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Notifications Dropdown */}
              {notificationsOpen && (
                <div className="absolute right-0 top-full mt-2 w-[calc(100vw-2rem)] max-w-sm animate-slide-down rounded-2xl border border-line bg-elevated shadow-elevation-4">
                  <div className="flex items-center justify-between border-b border-ink/[0.06] px-5 py-4">
                    <div>
                      <h3 className="font-semibold text-ink">Notificações</h3>
                      {unreadCount > 0 && (
                        <p className="text-xs text-ink/40">{unreadCount} não lida{unreadCount > 1 ? 's' : ''}</p>
                      )}
                    </div>
                    {unreadCount > 0 && (
                      <button
                        onClick={markAllAsRead}
                        disabled={markAllAsReadMutation.isPending}
                        className="rounded-lg px-3 py-1.5 text-xs font-medium text-brand-400 transition-colors hover:bg-brand-400/10 disabled:opacity-50"
                      >
                        {markAllAsReadMutation.isPending ? 'Marcando...' : 'Marcar todas'}
                      </button>
                    )}
                  </div>

                  <div className="max-h-[400px] overflow-y-auto">
                    {notifications && notifications.length > 0 ? (
                      notifications.map((notification) => {
                        const hasLink = !!getNotificationLink(notification);
                        return (
                          <div
                            key={notification.id}
                            onClick={() => hasLink && handleNotificationClick(notification)}
                            onKeyDown={(event) => {
                              if (hasLink && (event.key === 'Enter' || event.key === ' ')) {
                                event.preventDefault();
                                handleNotificationClick(notification);
                              }
                            }}
                            role={hasLink ? 'button' : undefined}
                            tabIndex={hasLink ? 0 : undefined}
                            className={cn(
                              'flex gap-3 border-b border-ink/[0.04] px-5 py-3.5 transition-colors last:border-0',
                              !notification.read && 'bg-brand-400/[0.04]',
                              hasLink && 'cursor-pointer hover:bg-ink/5'
                            )}
                          >
                            <NotificationIcon type={notification.type} className="mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <p className={cn(
                                'text-sm text-ink',
                                !notification.read && 'font-medium'
                              )}>
                                {notification.title}
                              </p>
                              <p className="mt-0.5 text-xs text-ink/50 line-clamp-2">
                                {notification.body}
                              </p>
                              <div className="mt-1.5 flex items-center gap-2">
                                <span className="text-xs text-ink/40">
                                  {formatTimeAgo(notification.createdAt)}
                                </span>
                                {hasLink && (
                                  <span className="flex items-center gap-0.5 text-xs font-medium text-brand-400">
                                    Ver <ChevronRight className="h-3 w-3" />
                                  </span>
                                )}
                              </div>
                            </div>
                            {!notification.read && (
                              <button
                                onClick={(e) => markAsRead(notification.id, e)}
                                disabled={markAsReadMutation.isPending}
                                className="shrink-0 self-start rounded-lg p-1 text-ink/30 transition-colors hover:bg-ink/5 hover:text-ink/80 disabled:opacity-50"
                                title="Marcar como lida"
                                aria-label="Marcar como lida"
                              >
                                <Check className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div className="px-5 py-12 text-center">
                        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-ink/[0.06]">
                          <BellOff className="h-6 w-6 text-ink/40" />
                        </div>
                        <p className="mt-4 text-sm font-medium text-ink">
                          Nenhuma notificação
                        </p>
                        <p className="mt-1 text-xs text-ink/40">
                          Você será notificado sobre novas propostas e mensagens
                        </p>
                      </div>
                    )}
                  </div>

                  {notifications && notifications.length > 0 && (
                    <div className="border-t border-ink/[0.06] p-3">
                      <Link
                        to="/dashboard/notificacoes"
                        className="flex items-center justify-center gap-1 rounded-xl py-2 text-sm font-medium text-brand-400 transition-colors hover:bg-brand-400/10"
                        onClick={() => setNotificationsOpen(false)}
                      >
                        Ver todas
                        <ChevronRight className="h-4 w-4" />
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </header>

        {/* Page content */}
        <main id="conteudo" className="flex-1 p-4 sm:p-6 lg:p-8">
          <div className="mx-auto max-w-7xl">
            {isAdmin && isMfaRequired ? <AdminMfaRequired /> : <Outlet />}
          </div>
        </main>
        <SiteFooter />
      </div>

      {/* Ponto de montagem para ações flutuantes (ex.: botão "Enviar feedback"). */}
      <div id="floating-actions" className="fixed bottom-4 right-4 z-40 flex flex-col items-end gap-2 sm:bottom-6 sm:right-6">
        <div style={{ order: 10 }}>
          <FeedbackButton />
        </div>
      </div>
    </div>
  );
}
